import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = 'lg'
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl'
  }[maxWidth];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/75 backdrop-blur-sm transition-opacity" 
        onClick={onClose} 
      />

      {/* Dialog Box */}
      <div className={`relative w-full ${maxWidthClasses} overflow-hidden rounded-3xl p-6 sm:p-8 shadow-ambient transition-all border
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-[#f5f5f0]
        light:bg-white light:border-[#D5E4DC] light:text-[#0D1C17]
      `}>
        <div className="flex items-start justify-between pb-4 border-b border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE]">
          <div>
            <h3 className="text-xl font-bold text-white dark:text-white light:text-[#0D1C17]">
              {title}
            </h3>
            {subtitle && (
              <p className="mt-1 text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-[#90cbb7] hover:text-white hover:bg-[#22493c] dark:hover:bg-[#22493c] light:hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6 max-h-[75vh] overflow-y-auto pr-1">
          {children}
        </div>
      </div>
    </div>
  );
};
